import {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { useTranslation } from 'react-i18next'
import { useThemeColors } from '@/contexts/ThemeContext'
import { useWalletContext } from '@/contexts/WalletContext'
import { useSlashCommands } from '@/hooks/chat/useSlashCommands'
import { useSpeechRecognition } from '@/hooks/chat/useSpeechRecognition'
import { setWalletContext } from '@/hooks/chat/commands/wallet'
import type { ProviderType } from '@/lib/ai-providers'
import { LoadingText, loadingPresets } from '@/lib/loadingAnimations'
import { theme } from '@/lib/theme'
import { handleMarkdownPaste } from '@/lib/markdownPaste'
import type { ChatMessageWithTimestamp } from '@/types/chat/ChatMessage'
import type { SlashCommand } from '@/types/chat/SlashCommand'
import Button from '../ui/Button'
import CommandDropdown from './CommandDropdown'
import useIsMobile from '@/hooks/useIsMobile'

const ASSISTANT_NAME = import.meta.env.VITE_ASSISTANT_NAME!

interface MessageInputProps {
  initialValue?: string
  isLoading: boolean
  isLoadingMessages?: boolean
  selectedProvider: ProviderType | null
  selectedModel: string | null
  isLoadingModels: boolean
  onSubmit: (value: string) => void
  onWebSearchToggle?: () => void
  onDisplayMessage?: (message: ChatMessageWithTimestamp) => void
  replyingTo?: ChatMessageWithTimestamp | null
  onClearReply?: () => void
  disabled?: boolean
  identityName: string
}

const MessageInput = memo(function MessageInput({
  initialValue = '',
  isLoading,
  isLoadingMessages = false,
  onSubmit,
  onWebSearchToggle,
  onDisplayMessage,
  replyingTo,
  onClearReply,
  disabled = false,
  identityName,
}: MessageInputProps) {
  // Own the input state locally to avoid parent re-renders on every keystroke
  const [input, setInput] = useState(initialValue)

  // Sync with initialValue when it changes (e.g., from URL ?q= param)
  useEffect(() => {
    if (initialValue) {
      setInput(initialValue)
    }
  }, [initialValue])

  const { t } = useTranslation()
  const colors = useThemeColors()

  // NOTE: useIsMobile detects touch devices for touch-optimized input sizing
  const isMobile = useIsMobile()
  const wallet = useWalletContext()
  const inputRef = useRef<HTMLTextAreaElement>(null)
  const gridContainerRef = useRef<HTMLDivElement>(null)
  const baseInputRef = useRef('')
  const undoStackRef = useRef<string[]>([])
  const [isComposing, setIsComposing] = useState(false)
  const [isFocused, setIsFocused] = useState(false)

  // Common styles for input and overlays to ensure perfect alignment
  const commonInputStyles = useMemo<React.CSSProperties>(() => ({
    fontSize: isMobile ? '14px' : theme.fontSize.md,
    fontFamily: theme.fontFamily.mono,
    lineHeight: '1.5',
    letterSpacing: 'normal',
    width: '100%',
    padding: 0,
    margin: 0,
    border: 'none',
    outline: 'none',
    backgroundColor: 'transparent',
    color: colors.primaryText,
    verticalAlign: 'top',
    display: 'block',
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
    appearance: 'none',
    WebkitAppearance: 'none',
  }), [isMobile, colors.primaryText])

  // Connect wallet context to slash commands
  useEffect(() => {
    setWalletContext({
      generateWallet: wallet.generateWallet,
      restoreWallet: wallet.restoreWallet,
      loadWallet: wallet.loadWallet,
      deleteWallet: wallet.deleteWallet,
      hasWallet: wallet.hasWallet,
      wallet: wallet.wallet,
    })
  }, [wallet.generateWallet, wallet.restoreWallet, wallet.loadWallet, wallet.deleteWallet, wallet.hasWallet, wallet.wallet])

  const handleRecognitionEnd = (
    finalTranscript: string,
    interimAtStop: string
  ) => {
    // When recognition ends, just update the input with the transcript
    const speechText = finalTranscript || interimAtStop || ''

    if (speechText.trim()) {
      const fullMessage = baseInputRef.current + speechText
      setInput(fullMessage)
    }

    // Clear the base input ref
    baseInputRef.current = ''
  }

  const {
    isListening,
    transcript,
    interimTranscript,
    isSupported,
    error: speechError,
    startListening,
    stopListening,
    resetTranscript,
    clearError: clearSpeechError,
  } = useSpeechRecognition({ onEnd: handleRecognitionEnd })
  
  // Auto-dismiss speech error after 5 seconds
  useEffect(() => {
    if (speechError) {
      const timeout = setTimeout(() => {
        clearSpeechError()
      }, 5000)
      return () => clearTimeout(timeout)
    }
  }, [speechError, clearSpeechError])

  const handleCommandExecute = (command: SlashCommand, args?: string) => {
    // Parse args from input if not provided
    let commandArgs = args
    if (!commandArgs && input.includes(' ')) {
      // Extract everything after the command name
      const parts = input.split(' ')
      commandArgs = parts.slice(1).join(' ')
    }

    setInput('')
    command.handler(commandArgs)
  }

  const handleSubmitMessage = useCallback(
    (message: string) => {
      // Submit the message directly and clear input
      onSubmit(message)
      setInput('')
    },
    [onSubmit]
  )

  const {
    isCommandMode,
    filteredCommands,
    availableCommands,
    selectedIndex,
    setSelectedIndex,
    handleKeyDown: handleSlashCommandKeyDown,
    selectCommand,
    isSearching,
  } = useSlashCommands({
    input,
    onCommandExecute: handleCommandExecute,
    onWebSearchToggle,
    onDisplayMessage,
    onSubmitMessage: handleSubmitMessage,
    onInputChange: setInput,
  })

  // Helper function to check if input is a slash command and execute it
  const tryExecuteSlashCommand = useCallback(
    (inputText: string): boolean => {
      if (!inputText.startsWith('/')) {
        return false
      }

      // Parse command name and arguments
      const parts = inputText.slice(1).split(' ')
      const commandName = parts[0].toLowerCase()
      const args = parts.slice(1).join(' ')

      // Find matching command in all available commands
      const command = availableCommands.find(
        (cmd) =>
          cmd.name === commandName ||
          cmd.aliases?.includes(commandName)
      )

      if (command) {
        // If command doesn't accept args, execute even if args are provided
        // If command does accept args, only execute if args are provided
        if (command.acceptsArgs === false || args.trim() !== '') {
          handleCommandExecute(command, args)
          return true
        }
      }

      return false
    },
    [availableCommands, handleCommandExecute]
  )

  useEffect(() => {
    if (!isLoading && inputRef.current) {
      inputRef.current.focus()
    }
  }, [isLoading])

  // Auto-resize textarea based on content
  useEffect(() => {
    const textarea = inputRef.current
    if (!textarea) return

    // Reset height to auto to get the correct scrollHeight
    textarea.style.height = 'auto'

    // Calculate the line height (approximately 1.5em for most monospace fonts)
    const lineHeight = parseFloat(getComputedStyle(textarea).lineHeight) || 24
    const maxLines = 5
    const maxHeight = lineHeight * maxLines

    // Set height based on content, but cap at max height
    const newHeight = Math.min(textarea.scrollHeight, maxHeight)
    textarea.style.height = `${newHeight}px`
  }, [input])

  // Update the input with speech transcript in real-time (for native speech recognition)
  useEffect(() => {
    if (isListening) {
      const fullTranscript =
        baseInputRef.current + transcript + interimTranscript
      setInput(fullTranscript)
    }
  }, [transcript, interimTranscript, isListening])

  const handleSpeechStart = () => {
    if (!isSupported || isLoading || isLoadingMessages) return

    // Store the current input as base for appending
    baseInputRef.current = input
    // Clear any previous transcript
    resetTranscript()
    startListening()
  }

  const handleSpeechEnd = () => {
    if (!isListening) return

    // Stop listening and pass the current interim transcript
    stopListening(interimTranscript)
  }

  const handleMouseDown = () => {
    handleSpeechStart()
  }

  const handleMouseUp = () => {
    handleSpeechEnd()
  }

  const handleMouseLeave = () => {
    if (isListening) {
      handleSpeechEnd()
    }
  }

  const handleTouchStart = (e: React.TouchEvent) => {
    e.preventDefault()
    handleSpeechStart()
  }

  const handleTouchEnd = (e: React.TouchEvent) => {
    e.preventDefault()
    handleSpeechEnd()
  }

  return (
    <div style={{ position: 'relative' }} data-chat-input-area>
      {/* Error banner */}
      {speechError && (
        <div
          style={{
            padding: '8px 12px',
            backgroundColor: colors.errorBg,
            borderBottom: `1px solid ${colors.errorBorder}`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '8px',
          }}
        >
          <div style={{ flex: 1, minWidth: 0 }}>
            <div
              style={{
                fontSize: '11px',
                color: colors.primaryText,
              }}
            >
              {speechError}
            </div>
          </div>
          <button
            onClick={clearSpeechError}
            style={{
              background: 'none',
              border: 'none',
              color: colors.primaryText,
              cursor: 'pointer',
              padding: '4px',
              fontSize: '14px',
              opacity: 0.7,
              lineHeight: 1,
            }}
            title="Dismiss"
          >
            ✕
          </button>
        </div>
      )}
      {/* Reply banner */}
      {replyingTo && (
        <div
          style={{
            padding: '8px 12px',
            backgroundColor: colors.secondaryBg,
            borderBottom: `1px solid ${colors.primaryBorder}`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '8px',
          }}
        >
          <div style={{ flex: 1, minWidth: 0 }}>
            <div
              style={{
                fontSize: '11px',
                color: colors.secondaryText,
                marginBottom: '2px',
              }}
            >
              {t('chat.input.replyingTo', { role: replyingTo.role })}
            </div>
            <div
              style={{
                fontSize: '12px',
                color: colors.primaryText,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {replyingTo.content.substring(0, 100)}
              {replyingTo.content.length > 100 ? '...' : ''}
            </div>
          </div>
          <button
            onClick={onClearReply}
            style={{
              backgroundColor: 'transparent',
              border: 'none',
              color: colors.secondaryText,
              cursor: 'pointer',
              fontSize: '16px',
              padding: '4px 8px',
              flexShrink: 0,
            }}
            title={t('chat.input.cancelReply')}
          >
            ✕
          </button>
        </div>
      )}
      {isCommandMode && (
        <CommandDropdown
          commands={filteredCommands}
          selectedIndex={selectedIndex}
          onSelect={selectCommand}
          onMouseEnter={setSelectedIndex}
        />
      )}
      <div
        style={{
          padding: isMobile ? '10px 12px' : theme.spacing.md,
          display: 'flex',
          // Vertically center the input row (caret, textarea, buttons) within the footer bar
          alignItems: 'center',
          gap: theme.spacing.sm,
          backgroundColor: colors.primaryBg,
          borderTop: `1px solid ${colors.primaryBorder}`,
        }}
        data-chat-input-root
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            height: isMobile ? '21px' : '24px',
            flexShrink: 0,
          }}
        >
          <span
            style={{
              color: colors.secondaryText,
              fontSize: isMobile ? '14px' : theme.fontSize.md,
              lineHeight: '1.5',
              fontFamily: theme.fontFamily.mono,
              // Small glyph-level tweak: visually center the caret within the line box
              // based on manual tuning (works for both desktop & mobile).
              position: 'relative',
              top: -1.5,
            }}
          >
            {'>'}
          </span>
        </div>
        <div
          ref={gridContainerRef}
          style={{ flex: 1, display: 'grid', gridTemplateColumns: '1fr', alignItems: 'start' }}
        >
          <textarea
            ref={inputRef}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onPaste={(e) => {
              const markdownContent = handleMarkdownPaste(e)
              if (markdownContent !== null) {
                e.preventDefault()

                const textarea = inputRef.current
                if (textarea) {
                  // Save current input so user can undo this programmatic change
                  undoStackRef.current.push(input)

                  const start = textarea.selectionStart
                  const end = textarea.selectionEnd
                  const before = input.substring(0, start)
                  const after = input.substring(end)
                  const newValue = before + markdownContent + after

                  setInput(newValue)

                  // Restore cursor position after React updates the textarea
                  setTimeout(() => {
                    textarea.selectionStart = textarea.selectionEnd =
                      start + markdownContent.length
                    textarea.focus()
                  }, 0)
                }
              }
            }}
            aria-label={t('chat.input.placeholder')}
            rows={1}
            data-slash-input
            style={{
              ...commonInputStyles,
              resize: 'none',
              overflowY: 'auto',
              minHeight: isMobile ? '21px' : '24px',
              gridArea: '1 / 1',
            }}
            disabled={isLoading || isLoadingMessages || isSearching || disabled}
            onFocus={() => setIsFocused(true)}
            onBlur={() => setIsFocused(false)}
            onCompositionStart={() => setIsComposing(true)}
            onCompositionEnd={() => setIsComposing(false)}
            onKeyDown={(e) => {
              // Don't handle Enter key during IME composition (e.g., Japanese input)
              if (isComposing) {
                return
              }

              // Handle slash command keyboard navigation first
              const commandHandled = handleSlashCommandKeyDown(e)
              if (commandHandled) {
                // If ESC was pressed in command mode, clear the input to close the menu
                if (e.key === 'Escape' && isCommandMode) {
                  setInput('')
                }
                return
              }

              // Ctrl+U: clear current input (shell-style, standard terminal keybinding)
              if (
                e.key === 'u' &&
                e.ctrlKey &&
                !e.metaKey &&
                !e.altKey &&
                !e.shiftKey
              ) {
                if (input) {
                  e.preventDefault()
                  // Save current input for undo
                  undoStackRef.current.push(input)
                  setInput('')
                }
                return
              }

              // Cmd/Ctrl+Z: undo last programmatic change (paste transform, clear)
              if (
                e.key === 'z' &&
                (e.metaKey || e.ctrlKey) &&
                !e.altKey &&
                !e.shiftKey
              ) {
                if (undoStackRef.current.length > 0) {
                  e.preventDefault()
                  const previousValue =
                    undoStackRef.current[undoStackRef.current.length - 1]
                  undoStackRef.current.pop()
                  setInput(previousValue)

                  // Restore caret to end of restored text
                  setTimeout(() => {
                    const textarea = inputRef.current
                    if (textarea) {
                      const pos = previousValue.length
                      textarea.selectionStart = textarea.selectionEnd = pos
                      textarea.focus()
                    }
                  }, 0)
                  return
                }
                // If we have nothing in our undo stack, fall through and let the browser handle native undo
              }

              // Regular input handling
              // Desktop: Enter sends, Shift+Enter for newlines
              // Mobile: Enter creates newline, use send button to send
              if (e.key === 'Enter' && !e.shiftKey && !isMobile) {
                e.preventDefault()

                // Check if this is a slash command first
                if (tryExecuteSlashCommand(input)) {
                  return
                }

                // Otherwise, submit as regular message
                const value = input.trim()
                if (value) {
                  onSubmit(value)
                  setInput('')
                }
              }
            }}
          />
          {/* Show loading animation when assistant is thinking */}
          {!input && (isLoadingMessages || isSearching || isLoading) && (
            <div
              aria-hidden="true"
              data-loading-text
              style={{
                ...commonInputStyles,
                gridArea: '1 / 1',
                pointerEvents: 'none',
                color: colors.secondaryText,
                userSelect: 'none',
              }}
            >
              <LoadingText
                {...loadingPresets.thinking(
                  isLoadingMessages
                    ? t('chat.input.loadingChat')
                    : isSearching
                      ? t('chat.input.searching', { name: identityName === 'default' ? ASSISTANT_NAME : identityName })
                      : t('chat.input.thinking', { name: identityName === 'default' ? ASSISTANT_NAME : identityName }),
                  colors.secondaryText
                )}
                fontSize={isMobile ? '14px' : theme.fontSize.md}
                style={{
                  // Only pass text styles, not layout styles to avoid double application
                  fontFamily: commonInputStyles.fontFamily,
                  lineHeight: commonInputStyles.lineHeight,
                  letterSpacing: commonInputStyles.letterSpacing,
                  whiteSpace: commonInputStyles.whiteSpace,
                  wordBreak: commonInputStyles.wordBreak,
                  color: colors.secondaryText,
                }}
              />
            </div>
          )}
          {/* Show placeholder only when not focused and not loading */}
          {!input && !isFocused && !(isLoadingMessages || isSearching || isLoading) && (
            <div
              aria-hidden="true"
              data-placeholder
              style={{
                ...commonInputStyles,
                gridArea: '1 / 1',
                pointerEvents: 'none',
                color: colors.secondaryText,
                userSelect: 'none',
              }}
            >
              {t('chat.input.placeholder')}
            </div>
          )}
        </div>
        <Button
          variant="secondary"
          size="md"
          onClick={(e) => {
            e.preventDefault()

            // Check if this is a slash command first
            if (tryExecuteSlashCommand(input)) {
              return
            }

            // Otherwise, submit as regular message
            const value = input.trim()
            if (value) {
              onSubmit(value)
              setInput('')
            }

            // Hide keyboard on mobile after sending
            if (isMobile) {
              inputRef.current?.blur()
            }
          }}
          disabled={
            !input.trim() ||
            isLoading ||
            isLoadingMessages ||
            isSearching ||
            disabled
          }
          isLoading={isLoading}
          style={{
            backgroundColor: colors.secondaryBg,
            color: colors.primaryText,
            fontWeight: 'medium',
            ...(isMobile && {
              borderRadius: '8px',
              height: '42px',
              minWidth: '60px',
              padding: '0 14px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '12px',
            }),
          }}
        >
          <span style={{ fontSize: isMobile ? '12px' : undefined, fontWeight: isMobile ? 600 : undefined }}>
            {t('chat.input.send')}
          </span>
        </Button>
        {/* Hide mic button on mobile - phones have built-in dictation */}
        {!isMobile && (
          <Button
            variant="secondary"
            size="md"
            disabled={
              !isSupported || isLoading || isLoadingMessages || isSearching
            }
            onMouseDown={handleMouseDown}
            onMouseUp={handleMouseUp}
            onMouseLeave={handleMouseLeave}
            onTouchStart={handleTouchStart}
            onTouchEnd={handleTouchEnd}
            style={{
              backgroundColor: isListening
                ? colors.primaryAccent
                : colors.secondaryBg,
              color: colors.primaryText,
              fontWeight: 'medium',
              cursor: isSupported ? 'pointer' : 'not-allowed',
              userSelect: 'none',
            }}
          >
            {isListening ? t('chat.input.listening') : t('chat.input.microphone')}
          </Button>
        )}
      </div>
    </div>
  )
})

export default MessageInput
